import { memo, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme, Badge, Icon, Popover } from '@Sage/hoi-poi-ui';
import { getDateFromBackendISO } from 'utils/dates';
import { getBackendBoolean } from 'utils/fm';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import SyncStatusBadgePopover from './SyncStatusBadgePopover';

import './styles.scss';

const ACTIONS = {
    SET_ENTITY_STATUS: 'SET_ENTITY_STATUS',
    SET_SYNC_STATUS: 'SET_SYNC_STATUS',
    SET_VISIBLE: 'SET_VISIBLE',
};

const SYNC_STATUS = {
    SYNCHRONIZED: 1,
    PENDING_TO_SYNC: 2,
    PENDING_TO_REVIEW: 3,
    NOT_SYNCHRONIZED: 4,
};

const ICON_PROPS = {
    className: 'fm-ssbadge__icon',
    size: 'small',
};

const BADGE_OVERRIDES = {
    Text: {
        style: {
            fontSize: 10,
            lineHeight: 1.2,
            letterSpacing: 0.06,
        },
    },
};

const initialState = {
    loading: true,
    entityStatus: null,
    syncStatus: null,
    visible: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_SYNC_STATUS:
            return { ...state, loading: false, syncStatus: action.syncStatus };
        case ACTIONS.SET_ENTITY_STATUS:
            return { ...state, loading: false, entityStatus: action.entityStatus };
        case ACTIONS.SET_VISIBLE:
            return { ...state, visible: action.visible };
        default:
            return state;
    }
};

const SyncStatusBadge = ({ entity, isDetail }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const contentRef = useRef(null);

    const { entityStatus, syncStatus, syncStatusEnabled } = useSelector((state) => {
        const entityDetail = state.entityDetail;
        const active = entityDetail.active;
        return {
            entityStatus: entityDetail[active]?.data?.syncStatus || null,
            syncStatus: state?.entitySyncStatus?.[entity.entity]?.data || null,
            syncStatusEnabled: getBackendBoolean(
                state?.config?.userData?.syncStatusEnabled || false,
            ),
        };
    });

    const theme = useTheme();

    const literals = useMemo(
        () => ({
            // Entity list sync status badge
            connected: getLiteral('label_connected'),
            notConnected: getLiteral('label_not_connected'),
            // Entity detail sync status badge
            syncSuccess: getLiteral('label_connector_entity_status_sync_ok'),
            syncPending: getLiteral('label_connector_entity_status_sync_pending'),
            syncPendingReview: getLiteral('label_connector_entity_status_sync_pending_review'),
            noSync: getLiteral('label_connector_entity_status_no_sync'),
            // Popover titles
            listPopoverTitle: getLiteral('label_connector_integrated_entity'),
            detailPopoverTitle: getLiteral('label_connector_integrated_record'),
            // Popover rows labels
            connectedWith: getLiteral('label_connected_with'),
            lastSyncDate: getLiteral('label_connector_last_sync_record_date'),
            connectionDetail: getLiteral('label_connector_integrated_entity_desc'),
            syncStatus: getLiteral('label_connector_sync_status'),
            syncDetail: getLiteral('label_connector_internalcomment'),
        }),
        [],
    );

    const onClickOutside = useCallback((e) => {
        if (!contentRef.current?.contains(e.target)) {
            dispatch({ type: ACTIONS.SET_VISIBLE, visible: false });
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [onClickOutside]);

    useEffect(() => {
        if (!syncStatusEnabled) return;

        if (!isDetail && syncStatus) {
            dispatch({ type: ACTIONS.SET_SYNC_STATUS, syncStatus });
        } else if (entityStatus?.statusId && entityStatus) {
            dispatch({
                type: ACTIONS.SET_ENTITY_STATUS,
                entityStatus,
            });
        }
    }, [entity.trueName, entityStatus, isDetail, syncStatus, syncStatusEnabled]);

    const formatDate = useCallback((date) => {
        return getDateFromBackendISO(date, 'DD/MM/YYYY HH:mm');
    }, []);

    const handleClick = useCallback(() => {
        const newVisibleState = !state.visible;
        dispatch({ type: ACTIONS.SET_VISIBLE, visible: newVisibleState });

        if (newVisibleState) {
            logEvent({
                event: entity.trueName,
                submodule: isDetail ? 'detail' : 'list',
                functionality: 'syncInfo',
            });
        }
    }, [entity.trueName, isDetail, state.visible]);

    const getBadgeTextWithIcon = useCallback(
        ({ text, iconName, color, color2 }) => (
            <span className="fm-ssbadge__inner">
                <span>{text}</span>
                <Icon name={iconName} color={color} color2={color2} {...ICON_PROPS} />
            </span>
        ),
        [],
    );

    const getPopoverRows = useCallback(
        ({ syncStatus, lastSyncDate, statusMessage }) =>
            [
                {
                    label: literals.syncStatus,
                    value: syncStatus,
                },
                !!lastSyncDate && {
                    label: literals.lastSyncDate,
                    value: formatDate(lastSyncDate),
                },
                !!statusMessage && {
                    label: literals.syncDetail,
                    value: getLiteral(statusMessage),
                },
            ].filter(Boolean),
        [literals, formatDate],
    );

    const { badgeProps, popoverProps } = useMemo(() => {
        // Entity list: connected
        if (!isDetail && state.syncStatus?.Connected) {
            return {
                badgeProps: {
                    type: 'semanticPositive',
                    children: literals.connected,
                },
                popoverProps: {
                    title: literals.listPopoverTitle,
                    rows: [
                        {
                            label: literals.connectedWith,
                            value: getLiteral(state.syncStatus.ConnectedWith),
                        },
                        !!state.syncStatus.LastSyncDate && {
                            label: literals.lastSyncDate,
                            value: formatDate(state.syncStatus.LastSyncDate),
                        },
                        !!state.syncStatus.Description && {
                            label: literals.connectionDetail,
                            value: getLiteral(state.syncStatus.Description),
                        },
                    ].filter(Boolean),
                },
            };
        }

        // Entity list: NOT connected
        if (!isDetail && state.syncStatus?.Connected === false) {
            return {
                badgeProps: {
                    type: 'semanticNegative',
                    children: literals.notConnected,
                },
            };
        }

        // Entity detail cases
        if (!isDetail || !state.entityStatus?.statusId) {
            return {};
        }

        const { statusId, lastSyncDate, statusMessage } = state.entityStatus;
        const basePopoverProps = {
            title: literals.detailPopoverTitle,
        };

        switch (statusId) {
            case SYNC_STATUS.SYNCHRONIZED:
                return {
                    badgeProps: {
                        type: 'semanticPositive',
                        children: getBadgeTextWithIcon({
                            text: literals.syncSuccess,
                            iconName: 'syncSuccess',
                            color: theme.colors.semantic.positive500,
                            color2: theme.colors.semantic.positive500,
                        }),
                    },
                    popoverProps: {
                        ...basePopoverProps,
                        rows: getPopoverRows({
                            syncStatus: literals.syncSuccess,
                            lastSyncDate,
                            statusMessage,
                        }),
                    },
                };

            case SYNC_STATUS.PENDING_TO_SYNC:
                return {
                    badgeProps: {
                        children: getBadgeTextWithIcon({
                            text: literals.syncPending,
                            iconName: 'syncPending',
                            color: theme.colors.semantic.grey[500],
                        }),
                    },
                    popoverProps: {
                        ...basePopoverProps,
                        rows: getPopoverRows({
                            syncStatus: literals.syncPending,
                            lastSyncDate,
                            statusMessage,
                        }),
                    },
                };

            case SYNC_STATUS.PENDING_TO_REVIEW:
                return {
                    badgeProps: {
                        type: 'semanticNegative',
                        children: getBadgeTextWithIcon({
                            text: literals.syncPendingReview,
                            iconName: 'syncInfo',
                            color: theme.colors.semantic.negative500,
                            color2: theme.colors.semantic.negative500,
                        }),
                    },
                    popoverProps: {
                        ...basePopoverProps,
                        rows: getPopoverRows({
                            syncStatus: literals.syncPendingReview,
                            lastSyncDate,
                            statusMessage,
                        }),
                    },
                };

            case SYNC_STATUS.NOT_SYNCHRONIZED:
                return {
                    badgeProps: {
                        type: 'semanticInfo',
                        children: getBadgeTextWithIcon({
                            text: literals.noSync,
                            iconName: 'syncInfo',
                            color: theme.colors.semantic.info500,
                            color2: theme.colors.semantic.info500,
                        }),
                    },
                    popoverProps: {
                        ...basePopoverProps,
                        rows: getPopoverRows({
                            syncStatus: literals.noSync,
                            lastSyncDate,
                            statusMessage,
                        }),
                    },
                };

            default:
                return {};
        }
    }, [
        literals,
        isDetail,
        state.syncStatus,
        state.entityStatus,
        theme,
        formatDate,
        getBadgeTextWithIcon,
        getPopoverRows,
    ]);

    const finalBadgeProps = useMemo(() => {
        const overrides = {
            ...BADGE_OVERRIDES,
            root: {
                style: {
                    cursor: popoverProps ? 'pointer' : 'default',
                },
            },
        };

        return {
            overrides,
            className: 'fm-ssbadge',
            onClick: popoverProps ? handleClick : null,
            isActive: state.visible,
            ...badgeProps,
        };
    }, [badgeProps, popoverProps, handleClick, state.visible]);

    const finalPopoverProps = useMemo(() => {
        return popoverProps
            ? {
                  className: 'fm-ssbadge__popover',
                  placement: 'bottom',
                  visible: state.visible,
                  onVisibilityChange: (visible) => dispatch({ type: ACTIONS.SET_VISIBLE, visible }),
                  content: <SyncStatusBadgePopover ref={contentRef} {...popoverProps} />,
              }
            : null;
    }, [popoverProps, state.visible]);

    if (!syncStatusEnabled || state.loading) return null;

    return !!finalPopoverProps ? (
        <Popover {...finalPopoverProps}>
            <Badge {...finalBadgeProps} />
        </Popover>
    ) : (
        <Badge {...finalBadgeProps} />
    );
};

export default memo(SyncStatusBadge);
